import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Ratio } from '@jr2os/shared'
import { ratioInsightKey, type RatioKey } from '../../../lib/ratio-insight'
import { STATUS_COLOR } from '../KpiDrilldownDrawer'
import { formatRatio, type RatioFormat } from './kpi-format'

/**
 * Tarjeta de ratio al estilo de Ledgeria: además del número y el color, dice en
 * una frase POR QUÉ está así.
 *
 * Antes solo había una barra de color y un `title` del navegador con la fórmula:
 * había que saberse los umbrales y pasar el ratón por encima para entender nada.
 * Ahora el estado se nombra (Óptimo / Precaución / Atención) y debajo va el
 * motivo concreto del valor actual; la fórmula y los umbrales siguen a un paso,
 * en el icono de información.
 */
export function ScoreCard({
  ratio,
  ratioKey,
  format,
  onClick,
}: {
  ratio: Ratio
  /** Clave del ratio: elige el texto del porqué. */
  ratioKey: RatioKey
  format: RatioFormat
  onClick: () => void
}) {
  const { t } = useTranslation('accounting')
  const color = STATUS_COLOR[ratio.status]
  const isNa = ratio.status === 'na' || ratio.value === null

  // El porqué se calcula del VALOR, no del semáforo, porque dos valores del
  // mismo color pueden estar así por motivos opuestos: en liquidez, ámbar es
  // "cobertura ajustada" por abajo y "recursos ociosos" por arriba.
  const why = t(`insight.${ratioKey}.${ratioInsightKey(ratioKey, ratio.value)}`, {
    defaultValue: '',
  })

  const detail = isNa
    ? t('tooltip.divByZero')
    : [
        t('tooltip.formula', { value: ratio.formula }),
        t('tooltip.thresholds', { value: ratio.thresholds }),
        ratio.tooltip,
      ].join('\n')

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative text-left rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4 hover:bg-[var(--bg-hover)] transition-colors focus:outline-none focus:ring-2 focus:ring-jr2-500/60 flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider t-muted leading-tight">
          {ratio.label}
        </span>
        {/* La fórmula y los umbrales dejan de ocupar sitio, pero siguen a mano. */}
        <span title={detail} className="shrink-0 t-faint hover:t-secondary cursor-help">
          <Info size={13} />
        </span>
      </div>

      <div
        className="text-2xl font-semibold font-mono mt-2"
        style={{ color: isNa ? 'var(--text-muted, #64748b)' : undefined }}
      >
        {formatRatio(ratio.value, format)}
      </div>

      <div className="flex items-center gap-1.5 mt-1.5">
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: color }}
          aria-hidden
        />
        <span className="text-[11px] font-medium" style={{ color }}>
          {t(`insight.status.${ratio.status}`)}
        </span>
      </div>

      {why && <p className="text-[11px] t-secondary leading-snug mt-1.5 line-clamp-3">{why}</p>}
    </button>
  )
}
